/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, Inject, Input, OnInit, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {UIRouter} from '@uirouter/core';

import template from './creative-bulk-upload-page.component.html';
import {applyHalfSizeDimensions, CreativeBulkUploadBehaviorService} from './creative-bulk-upload-setup';
import {CreativeBulkActionService} from './creative-bulk-action.service';
import {CreativeAttachModalService} from './creative-attach-modal.service';
import {CreativeCopyInBulkModalService} from './creative-copy-in-bulk-modal.service';
import {createFormSubmitState, exposeFormSubmitStateAccessors, FormSubmitState} from '../common/controller/form-submit-state';
import {asUrlGroupList, asUrlList} from './creative-url-utils';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {CreativeApiService} from './creative-api.service';
import {LayoutApiService} from '../admin/layout/layout-api.service';
import {LibraryCreativeApiService} from '../library-creative/library-creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {AssetService} from './asset/asset.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {compareById} from '../common/util/object-id-comparison';

import {NavigationHistoryService} from '../../components/util/navigation-history.service';

@Component({
  standalone: false,
  selector: 'adn-creative-bulk-upload-page',
  template
})
export class CreativeBulkUploadPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  [key: string]: any;

  private _creativeTypeDirect: string | undefined;
  private setupInitialized = false;
  private activeNgForm: NgForm | null = null;

  submitState: FormSubmitState = createFormSubmitState();
  readonly viewRevision = signal(0);

  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  @Input()
  set creativeType(value: string) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): string {
    return this._creativeTypeDirect;
  }

  constructor(
    @Inject(AssetService) private readonly Asset: any,
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService,
    private readonly teamApi: TeamApiService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LibraryCreativeApiService) private readonly LibraryCreative: LibraryCreativeApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeAttachModalService: CreativeAttachModalService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly creativeBulkUploadBehaviorService: CreativeBulkUploadBehaviorService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState);
  }

  get formSubmitPristine(): boolean {
    return this.activeNgForm?.pristine ?? true;
  }

  ngOnInit(): void {
    if (this.setupInitialized || !this.creativeType) {
      return;
    }
    this.setupInitialized = true;
    this.creativeBulkUploadBehaviorService.initialize(this, {
      creativeType: this.creativeType,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      Asset: this.Asset,
      Layout: this.Layout,
      teamApi: this.teamApi,
      LineItem: this.LineItem,
      LocalNetworkProfile: this.LocalNetworkProfile,
      LibraryCreative: this.LibraryCreative,
      Creative: this.Creative,
      listBinding: this.listBinding,
      listLifetime: this.listLifetime,
      LocalUserPermissions: this.LocalUserPermissions,
      creativeBulkActionService: this.creativeBulkActionService,
      creativeAttachModalService: this.creativeAttachModalService,
      creativeCopyInBulkModalService: this.creativeCopyInBulkModalService,
      localNetworkInfo: this.localNetworkInfo
    });
  }

  readonly compareById = compareById;

  private readonly objectKeysCache = new WeakMap<object, string[]>();

  objectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }
    const keys = Object.keys(obj);
    const cached = this.objectKeysCache.get(obj);
    if (cached && cached.length === keys.length && cached.every((key, i) => key === keys[i])) {
      return cached;
    }
    this.objectKeysCache.set(obj, keys);
    return keys;
  }

  isTeamSelected(creative: any, teamId: string): boolean {
    return !!(creative?.teams && creative.teams.indexOf(teamId) > -1);
  }

  toggleTeam(creative: any, teamId: string, event: Event): void {
    const checked = !!(event.target as HTMLInputElement).checked;
    creative.teams = creative.teams || [];
    if (checked && creative.teams.indexOf(teamId) < 0) {
      creative.teams.push(teamId);
    } else if (!checked) {
      creative.teams = creative.teams.filter((id: string) => id !== teamId);
    }
  }

  toggleHalfSize(creative: any, event: Event): void {
    const halfSize = !!(event.target as HTMLInputElement).checked;
    applyHalfSizeDimensions(creative, halfSize);
    this.activeNgForm?.control.markAsDirty();
  }

  firstLineItemKey(creative: any): string {
    return creative?.lineItems ? Object.keys(creative.lineItems)[0] : '';
  }

  get currentSubTypes(): string[] {
    return (this.validSubTypes && this.validSubTypes[this.type] && this.validSubTypes[this.type].asArray) || [];
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }

  setBulkForm(form: NgForm): string {
    this.activeNgForm = form;
    this.submitState.ngForm = form;
    if (this.setForm) {
      this.setForm(form);
    }
    return '';
  }
}
